import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wine, Star, TrendingUp, ClipboardList, Plus, ArrowRight } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { categoriaPuntuacion } from "@/lib/tastingOptions";
import InstallPrompt from "@/components/InstallPrompt";

const PIE_COLORS = ["#722F37", "#D4AF37", "#800020", "#C5A059", "#4E070C", "#9CA3AF"];
const TOOLTIP_STYLE = { background: "#0A0A0A", border: "none", borderRadius: 8, color: "#fff" };
const BAR_CURSOR = { fill: "rgba(114,47,55,0.06)" };
const AXIS_TICK = { fontSize: 11, fill: "#6B7280" };
const BAR_RADIUS = [6, 6, 0, 0];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-6 border-grises-200 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-transform duration-300">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.15em] text-grises-500">{label}</span>
        <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={1.75} />
      </div>
      <div className="font-mono text-3xl font-bold text-negro">{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });
  const { data: usage } = useQuery({
    queryKey: ["usage"],
    queryFn: async () => (await api.get("/usage")).data,
  });

  if (isLoading || !data)
    return <div className="animate-pulse text-grises-400">Cargando panel...</div>;

  const empty = data.total_tastings === 0 && data.total_wines === 0;

  return (
    <div className="animate-fade-up">
      <InstallPrompt />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-1">Panel</p>
          <h1 className="font-serif text-4xl text-negro">Tu bodega de catas</h1>
          {usage && usage.limit !== null && (
            <p data-testid="dashboard-usage" className="text-sm text-grises-500 mt-2">
              Plan gratuito · <span className={usage.reached ? "text-borgona-700 font-medium" : "text-negro font-medium"}>{usage.count}/{usage.limit}</span> catas registradas
            </p>
          )}
        </div>
        <Button data-testid="dashboard-new-tasting-btn" onClick={() => navigate("/app/catas/nueva")}
          className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full transition-colors duration-300">
          <Plus className="w-4 h-4 mr-1" /> Nueva Cata
        </Button>
      </div>

      {empty ? (
        <Card className="p-12 text-center border-grises-200 rounded-xl">
          <Wine className="w-14 h-14 text-borgona-300 mx-auto mb-4" strokeWidth={1.25} />
          <h3 className="font-serif text-2xl text-negro mb-2">Aún no hay catas</h3>
          <p className="text-grises-500 mb-6">Registra tu primer vino y comienza a catar.</p>
          <Button data-testid="empty-new-tasting-btn" onClick={() => navigate("/app/catas/nueva")}
            className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Crear primera cata
          </Button>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <StatCard icon={ClipboardList} label="Catas" value={data.total_tastings} accent="#722F37" />
            <StatCard icon={Wine} label="Vinos" value={data.total_wines} accent="#800020" />
            <StatCard icon={Star} label="Mejor puntuación" value={data.best_score} accent="#D4AF37" />
            <StatCard icon={TrendingUp} label="Promedio" value={data.avg_score} accent="#C5A059" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
            <Card className="p-6 lg:col-span-2 border-grises-200 rounded-xl">
              <h3 className="font-serif text-xl text-negro mb-5">Países</h3>
              {data.paises.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.paises.slice(0, 8)}>
                    <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={BAR_CURSOR} />
                    <Bar dataKey="value" fill="#722F37" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-grises-400 text-sm">Sin datos</p>}
            </Card>

            <Card className="p-6 border-grises-200 rounded-xl">
              <h3 className="font-serif text-xl text-negro mb-5">Tipos de vino</h3>
              {data.tipos.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={data.tipos} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                      {data.tipos.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-grises-400 text-sm">Sin datos</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                {data.tipos.map((t, i) => (
                  <span key={t.name} className="text-xs text-grises-600 flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    {t.name}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          <Card className="p-6 border-grises-200 rounded-xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif text-xl text-negro">Últimas catas</h3>
              <Link to="/app/historial" data-testid="see-all-tastings-link" className="text-sm text-borgona-700 hover:underline flex items-center gap-1">
                Ver todo <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-grises-100">
              {data.recent.map((t) => {
                const cat = categoriaPuntuacion(t.puntuacion);
                return (
                  <Link key={t.id} to={`/app/catas/${t.id}`} data-testid={`recent-tasting-${t.id}`}
                    className="flex items-center justify-between py-4 group">
                    <div>
                      <div className="font-medium text-negro group-hover:text-borgona-700 transition-colors">{t.wine?.nombre || "Vino"}</div>
                      <div className="text-sm text-grises-500">{t.wine?.bodega} · {t.wine?.anada}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      {t.status === "borrador" && <span className="text-xs px-2 py-0.5 rounded-full bg-grises-100 text-grises-500">Borrador</span>}
                      <span className="font-mono text-lg font-bold" style={{ color: cat.color }}>{t.puntuacion}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
